/**
 * Add Ad Set modal (#6211) — grow an existing Meta campaign with a new ad set + composed ads.
 *
 * The backend re-derives the delivery stack (outcome, promoted object, any instant form) from the
 * campaign, and copy is generated per angle with rotated hooks (#6210) — so the modal collects only
 * what makes the new ad set its own: the images (one ad per image, the wizard's picker cap) and,
 * for ABO campaigns, its daily budget. Targeting starts from the business's locations and is
 * adjusted afterwards on the Targeting tab. Creation is async: CREATE dispatches, the modal stays
 * up polling the task status, and closes into the refreshed campaign on completion.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/glitch-ui/spinner';
import { ImageGallery } from '@/components/glitch-ui/image-gallery';
import { FieldError } from '@/components/glitch-ui/field-error';
import { META_MAX_CREATIVES, toggleCreativeSelection } from '@/lib/meta';
import { metaAdSetTaskStatusQueryOptions, useCreateMetaAdSetMutation } from '@/lib/query-options';
import type { MetaCampaignDetailResponse } from '@/lib/types';

export function AddMetaAdSetModal({
  campaign,
  currencySymbol,
  onClose,
}: {
  campaign: MetaCampaignDetailResponse;
  currencySymbol: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const createMutation = useCreateMetaAdSetMutation();
  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);
  const [budgetWhole, setBudgetWhole] = useState(0);
  const [assetsMarked, setAssetsMarked] = useState(false);
  const [budgetMarked, setBudgetMarked] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  // Dispatch accepted — the modal switches to its creating phase and polls the task.
  const [isCreating, setIsCreating] = useState(false);

  const taskStatus = useQuery(metaAdSetTaskStatusQueryOptions(campaign.slug, isCreating));
  useEffect(() => {
    if (!isCreating) return;
    if (taskStatus.data?.state === 'COMPLETED') {
      // The new ad set exists — refresh the tree (sidebar tier, Ad Sets table, ad-set surface).
      void queryClient.invalidateQueries({ queryKey: ['meta', 'campaign', campaign.slug] });
      void queryClient.invalidateQueries({ queryKey: ['meta', 'campaigns'] });
      onClose();
    } else if (taskStatus.data?.state === 'FAILED') {
      setIsCreating(false);
      setServerError(taskStatus.data.error_message || 'Ad-set creation failed. Try again.');
    }
  }, [campaign.slug, isCreating, onClose, queryClient, taskStatus.data]);

  const handleCreate = async () => {
    const missingAssets = selectedAssetIds.length === 0;
    const missingBudget = !campaign.is_cbo && budgetWhole <= 0;
    setAssetsMarked(missingAssets);
    setBudgetMarked(missingBudget);
    if (missingAssets || missingBudget) return;
    setServerError(null);
    try {
      // Reset the status cache BEFORE dispatch so a previous run's terminal state can't
      // short-circuit the polling effect into closing/failing instantly.
      queryClient.removeQueries({ queryKey: ['meta', 'ad-set-task-status', campaign.slug] });
      await createMutation.mutateAsync({
        slug: campaign.slug,
        body: {
          ads: selectedAssetIds.map((id) => ({ creative_asset_id: id })),
          ...(campaign.is_cbo ? {} : { daily_budget_cents: budgetWhole * 100 }),
        },
      });
      setIsCreating(true);
    } catch (error) {
      setServerError(error instanceof Error ? error.message : 'The ad set could not be created.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !isCreating && onClose()}>
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden sm:max-w-[760px]"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Add Ad Set</DialogTitle>
          <p className="text-sm text-gray-600">
            One ad is created per image, up to {META_MAX_CREATIVES}. Copy is generated per angle and edited on
            the ad set afterwards.
          </p>
        </DialogHeader>
        <div className="flex-1 space-y-4 overflow-y-auto py-4">
          {isCreating ? (
            <div className="flex items-center gap-3 py-8 text-sm text-gray-900">
              <Spinner className="h-5 w-5" />
              Creating the ad set. This can take a minute; the campaign refreshes when it lands.
            </div>
          ) : (
            <>
              <div>
                {assetsMarked && (
                  <div className="mb-1 flex justify-end">
                    <FieldError message="Pick at least one image" />
                  </div>
                )}
                <ImageGallery
                  title=""
                  baseText=""
                  pageSize={50}
                  mode="picker"
                  selectedAssetIds={selectedAssetIds}
                  onSelect={(asset) => {
                    setAssetsMarked(false);
                    setSelectedAssetIds((prev) => toggleCreativeSelection(prev, asset.id));
                  }}
                />
                <p className="text-right text-sm text-gray-500">
                  {selectedAssetIds.length} of {META_MAX_CREATIVES} selected
                </p>
              </div>
              {!campaign.is_cbo ? (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-900">Daily budget for the new ad set:</span>
                  <div className="flex items-center gap-1">
                    {budgetMarked && <FieldError message="Required" />}
                    <span className="text-sm text-gray-500">{currencySymbol}</span>
                    <Input
                      type="number"
                      min={1}
                      value={budgetWhole || ''}
                      error={budgetMarked}
                      onChange={(e) => {
                        setBudgetMarked(false);
                        setBudgetWhole(Number(e.target.value));
                      }}
                      className="w-28 text-right"
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  This campaign uses CBO: the campaign budget is shared across all ad sets.
                </p>
              )}
              <p className="text-sm text-gray-500">
                The new ad set starts with the business's location targeting and starts paused. Adjust targeting
                on the Targeting tab after creation.
              </p>
              {serverError && (
                <div className="border-l-4 border-[#FCA5A5] bg-[#FEE2E2] px-3 py-2 text-sm text-[#B91C1C]">
                  {serverError}
                </div>
              )}
            </>
          )}
        </div>
        {!isCreating && (
          <DialogFooter>
            <Button variant="outline" className="h-9 text-gray-900" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="h-9 bg-[#290BD9] hover:bg-[#2309C5]"
              onClick={handleCreate}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating…' : 'Create Ad Set'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
